/**
 * ScrollBar.js
 * Created on 23-sep-2013
 * 
 * A wrapper class for (horizontal and vertical) sliders.
 * Works with createjs and exported skins from flash.
 * Skin should have instance 'dragButton' (see Button) and 'dragArea'.
 * 
 * @author Reinier Feijen for Ranj
 **/

import Button from "../buttons/Button";

export default class ScrollBar {
    /**
     * @constructor
     * @param {createjs.Container} skin Container containing instances:
     *   'dragButton' 
     *   'dragArea' 
     *   'scrollUpButton' (optional) button to scroll up  ! NOT IMPLEMENTED !
     *   'scrollDownButton' (optional) button to scroll down  ! NOT IMPLEMENTED !
     * @param {boolean} isVertical Optional, boolean indicating slider is vertical (default = true).
     * @param {number} padding Optional, padding between top and bottom of the scroll area and the top and bottom of the dragButton (default = 0).
     */
    constructor(skin, isVertical, padding) {
        createjs.EventDispatcher.initialize(this);

        this.skin = skin;
        this.button = new Button(skin.dragButton);
        this.bar = skin.dragArea;
        this._barEnabled = true;

        if (typeof (padding) === 'undefined') padding = 0;
        if (typeof (isVertical) === 'undefined') isVertical = true;
        this._x = isVertical ? "y" : "x";
        this._regX = isVertical ? "regY" : "regX";
        this._width = isVertical ? "height" : "width";
        this._scaleX = isVertical ? this.bar.scaleY : this.bar.scaleX;

        const left = this.bar[this._x] - this.bar[this._regX];
        this._min = left + this.bar.nominalBounds[this._x] * this._scaleX + padding;
        this._max = left + (this.bar.nominalBounds[this._x] + this.bar.nominalBounds[this._width]) * this._scaleX - padding;

        this._handleMouseDownBound = this._handleMouseDown.bind(this);
        this.button.addEventListener("mousedown", this._handleMouseDownBound);
        this._handleMouseMoveBound = this._handleMouseMove.bind(this);
        this.button.addEventListener("pressmove", this._handleMouseMoveBound);
        this._handleMouseUpBound = this._handleMouseUp.bind(this);
        this.button.addEventListener("pressup", this._handleMouseUpBound);
        this._handleBarClickBound = this._handleBarClick.bind(this);
        this.bar.addEventListener("click", this._handleBarClickBound);

        this.setEnabled(true);
        this.setValue(0);
    }

    destroy() {
        this.removeAllEventListeners();
        this.button.removeEventListener("mousedown", this._handleMouseDownBound);
        this.button.removeEventListener("pressmove", this._handleMouseMoveBound);
        this.button.removeEventListener("mouseup", this._handleMouseUpBound);
        this.button.destroy();
        this.bar.removeEventListener("click", this._handleBarClickBound);
    }

    getEnabled() {
        return this._enabled;
    }

    setEnabled(bool) {
        this._enabled = bool;
        this._dragging = false;
        this.button.setEnabled(bool);
    }

    getVisible() {
        return this.skin.visible;
    }

    setVisible(bool) {
        this.skin.visible = bool;
    }

    /**
     * En/disable clicks on bar. Default enabled.
     * @param {Boolean} bool
     * @returns {void}
     */
    setBarEnabled(bool) {
        this._barEnabled = bool;
    }

    getBarEnabled() {
        return this._barEnabled;
    }

    setValue(value) {
        this._value = Math.max(0, Math.min(1, value));
        this._updateValue();
    }

    getValue() {
        return this._value;
    }

    // set min by hand
    setMin(min) {
        this._min = min;
    }

    getMin() {
        return this._min;
    }

    // set max by hand
    setMax(max) {
        this._max = max;
    }

    getMax() {
        return this._max;
    }

    _handleMouseDown({ stageX, stageY }) {
        if (!this._enabled) return;
        const pnt = this.bar.parent.globalToLocal(stageX, stageY);
        this.offset = pnt[this._x] - this.button.skin[this._x];
        this._dragging = true;
    }

    _handleMouseMove({ stageX, stageY }) {
        if (!this._enabled) return;
        const pnt = this.bar.parent.globalToLocal(stageX, stageY);
        pnt[this._x] -= this.offset;
        this.setValue((pnt[this._x] - this._min) / (this._max - this._min));
        this.dispatchEvent("change");
    }

    _handleMouseUp(e) {
        if (!this._enabled) return;
        this._dragging = false;
        this.dispatchEvent("complete");
    }

    _handleBarClick({ stageX, stageY }) {
        if (!this._enabled || !this._barEnabled) return;
        const pnt = this.bar.parent.globalToLocal(stageX, stageY);
        this.setValue((pnt[this._x] - this._min) / (this._max - this._min));
        this.dispatchEvent("change");
    }

    _updateValue() {
        this.button.skin[this._x] = this._min + (this._max - this._min) * this._value;
    }
}